import { Mixpanel } from "mixpanel-react-native"
import type { MixpanelEventMap, MixpanelEventName } from "@ecountabl/lib"

type AnalyticsInitParams = {
  trackAutomaticEvents: boolean
  mixpanelId: string
  version: string
  env: string
  session_id: string
  platform: string
}

type EventsWithOptionalProps = {
  [K in MixpanelEventName]: MixpanelEventMap[K] extends undefined ? K : never
}[MixpanelEventName]

type EventsWithRequiredProps = Exclude<MixpanelEventName, EventsWithOptionalProps>

class Analytics {
  private static mixpanel: Mixpanel | null = null
  static init(params: AnalyticsInitParams): Promise<void> {
    if (Analytics.mixpanel) {
      return
    }

    const {
      mixpanelId,
      trackAutomaticEvents,
      version,
      env,
      session_id,
      platform
    } = params

    Analytics.mixpanel = new Mixpanel(mixpanelId, trackAutomaticEvents)

    Analytics.mixpanel.init()

    Analytics.mixpanel.setLoggingEnabled(false)

    Analytics.mixpanel.registerSuperProperties({
      version,
      env,
      session_id,
      platform,
      origin: 'app',
    })
  }

  static track<E extends EventsWithOptionalProps>(
    eventName: E,
    properties?: MixpanelEventMap[E]
  ): void
  static track<E extends EventsWithRequiredProps>(
    eventName: E,
    properties: MixpanelEventMap[E]
  ): void
  static track<E extends MixpanelEventName>(
    eventName: E,
    properties?: MixpanelEventMap[E]
  ): void {
    if (!Analytics.mixpanel) {
      return
    }

    Analytics.mixpanel.track(eventName as string, properties as Record<string, unknown> | undefined)
  }

  /**
   * Alias an existing distinct id to a new one.
   * Used to tie anonymous pre-auth events to an authenticated user id.
   */
  static alias(newDistinctId: string, previousDistinctId?: string): void {
    if (!Analytics.mixpanel) return
    const mp = Analytics.mixpanel as unknown as {
      alias?: (alias: string, original?: string) => void
    }
    if (typeof mp.alias !== 'function') return
    try {
      mp.alias(newDistinctId, previousDistinctId)
    } catch {
      // ignore alias failures (can occur if alias already exists)
    }
  }

  // mixpanel people methods
  static identifyUser(distinctId: string): void {
    if (!Analytics.mixpanel) {
      return
    }

    Analytics.mixpanel.identify(distinctId)
  }

  static setUserProperties(properties: Record<string, unknown>): void {
    if (!Analytics.mixpanel) {
      return
    }

    Analytics.mixpanel.getPeople().set(properties)
  }

  static setUserGroup(group: string, groupId: unknown): void {
    if (!Analytics.mixpanel) {
      return
    }

    if (groupId === undefined || groupId === null) return
    Analytics.mixpanel.setGroup(group, String(groupId))
  }
  /**
   * Used to reset the analytics session and clear the user identity associated with the session
   * @returns void
   */
  static reset(): void {
    if (!Analytics.mixpanel) {
      return
    }

    Analytics.mixpanel.reset()
  }

  static flush(): void {
    if (!Analytics.mixpanel) {
      return
    }

    Analytics.mixpanel.flush()
  }
}



export default Analytics